import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type {
  IntervalListPublicApiQueryOptions,
  IntervalListResponse,
} from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import type { UseInfiniteQueryOptions } from "@tanstack/react-query"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"

import { getIntervals } from "./get-intervals"

interface UseIntervalsInfiniteOptions {
  apiQueryOptions?: IntervalListPublicApiQueryOptions
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseInfiniteQueryOptions<IntervalListResponse, HttpError>,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam" | "select"
  >
}

export const useIntervalsInfinite = ({
  apiQueryOptions = {},
  organizationId,
  queryOptions = {},
}: UseIntervalsInfiniteOptions) => {
  const { placeholderData, ...restQueryOptions } = queryOptions

  const queryKey = useQueryKey(ApiQueryName.Intervals, "getMany", null, {
    apiQueryOptions,
    organizationId,
  })

  const queryResult = useInfiniteQuery<IntervalListResponse, HttpError>({
    ...restQueryOptions,
    enabled: Boolean(organizationId) && (restQueryOptions.enabled ?? true),
    placeholderData: placeholderData ?? keepPreviousData,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const offset = Number(pageParam)

      if (!organizationId) {
        throw new Error("organizationId is required to fetch intervals")
      }

      const response = await getIntervals({
        apiQueryOptions: {
          ...apiQueryOptions,
          offset,
        },
        organizationId,
      })

      return {
        ...response,
        offset,
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.items.length === 0) {
        return undefined
      }

      const nextOffset = lastPage.offset + lastPage.items.length
      if (nextOffset >= lastPage.total) {
        return undefined
      }
      return nextOffset
    },
    throwOnError: restQueryOptions.throwOnError ?? false,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Intervals)
}
